/**
 * Playlist item schedules for Apple TV signage (FranchiseSign).
 *
 * `startDate` and `endDate` are whole days, `YYYY-MM-DD` or null, both
 * inclusive. There is no time and no time zone: each Apple TV compares them
 * with its own local date. So nothing here goes through `toISOString()` or UTC,
 * which would move a date picked in the evening in the Americas to the next
 * day. Dates are built from, and compared as, local calendar parts.
 *
 * Parsing and today's date come from the site-wide date picker's helpers.
 */
import { parseIsoDate, todayIsoDate } from '@/components/ui/CalendarDatePicker';

export type ScheduleStatus = 'always' | 'scheduled' | 'active' | 'expired';

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** A real calendar date in exactly YYYY-MM-DD form ("2026-02-30" is not). */
export function isIsoDate(value: string): boolean {
  return value === value.trim() && parseIsoDate(value) !== null;
}

/** A picker's value as the API wants it: the date as picked, or null when cleared. */
export function toApiDate(value: string | null | undefined): string | null {
  const v = (value ?? '').trim();
  return v === '' ? null : v;
}

/**
 * Why this range would be refused, mirroring the API's 400s, or null when it
 * is fine. Empty means no bound. Same-day ranges are allowed (a one-day promo).
 */
export function scheduleRangeError(start: string | null, end: string | null): string | null {
  if (start && !isIsoDate(start)) return 'Start date must be a real date';
  if (end && !isIsoDate(end)) return 'End date must be a real date';
  // YYYY-MM-DD strings sort the same as the dates they name.
  if (start && end && end < start) return 'End date must be on or after the start date';
  return null;
}

/** Where the item stands today. Both bounds inclusive; no dates is "always". */
export function scheduleStatus(
  start: string | null,
  end: string | null,
  today: string = todayIsoDate(),
): ScheduleStatus {
  if (!start && !end) return 'always';
  if (start && today < start) return 'scheduled';
  if (end && today > end) return 'expired';
  return 'active';
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "Mar 5, 2026" from "2026-03-05", without going through a Date. */
export function formatScheduleDate(iso: string): string {
  const m = ISO_DATE.exec(iso);
  if (!m) return iso;
  return `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}, ${m[1]}`;
}

/** One line describing the window, e.g. "Mar 5, 2026 – Mar 9, 2026", "From …", "Until …". */
export function formatSchedule(start: string | null, end: string | null): string {
  if (start && end) {
    return start === end
      ? `${formatScheduleDate(start)} only`
      : `${formatScheduleDate(start)} – ${formatScheduleDate(end)}`;
  }
  if (start) return `From ${formatScheduleDate(start)}`;
  if (end) return `Until ${formatScheduleDate(end)}`;
  return 'Always';
}
